/**
 * B2B Lead Form Web Component
 *
 * Supports three variants:
 * - single        : single-page form (landing page)
 * - multi-step    : two-step form with trin 1 segmentation (5 subpages)
 * - notifikation  : email-only waitlist (Custom & Logo coming-soon)
 *
 * Reuses submission logic and silent spam protection from first-pass implementation:
 * - Honeypot field
 * - Time-gate (2000ms minimum)
 * - Interaction-gate (mouse/touch/keyboard required)
 * - Stub mode toggle (console.log vs fetch)
 * - CRM response handling (201, 400, 429, 500, network error)
 *
 * User-facing strings are read from:
 * - data-* attributes on the host <b2b-lead-form> element (set in Liquid via {{ t }})
 * - window.SC_B2B_LABELS bridge (set in Liquid via inline script)
 *
 * Used on: landing page (page.business.json) + 6 B2B subpages
 */

// ============================================
// CONSTANTS
// ============================================

// Free email provider blacklist — mirrors CRM team's server-side list.
// TODO(CRM): update if CRM team expands the list.
const FREE_EMAIL_PROVIDERS = new Set([
  'gmail.com', 'hotmail.com', 'hotmail.dk', 'yahoo.com', 'yahoo.dk',
  'outlook.com', 'outlook.dk', 'live.com', 'live.dk', 'icloud.com',
  'me.com', 'mac.com', 'aol.com', 'protonmail.com', 'proton.me',
  'mail.ru', 'yandex.com', 'gmx.com', 'gmx.dk'
]);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[\d+\s-]+$/;
const TIME_GATE_MS = 2000;
const NOTIFICATION_PLACEHOLDER_PHONE = '+45 00 00 00 00';
const AUTO_ADVANCE_DELAY_MS = 300;

// Read localized labels from the Liquid-injected bridge.
// Provides safe ASCII fallbacks if the bridge is unavailable (shouldn't happen in prod).
function getLabels() {
  return (typeof window !== 'undefined' && window.SC_B2B_LABELS) || {};
}

function formatStepOf(current, total) {
  const labels = getLabels();
  const tpl = labels.step_of_template || 'Step __CURRENT__ of __TOTAL__';
  return tpl.replace('__CURRENT__', String(current)).replace('__TOTAL__', String(total));
}

// ============================================
// VALIDATION HELPERS
// ============================================

function validateRequired(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function validateLength(value, min, max) {
  const len = (value || '').trim().length;
  return len >= min && len <= max;
}

function validateEmail(value) {
  const email = (value || '').trim().toLowerCase();
  if (!EMAIL_REGEX.test(email)) return { ok: false, reason: 'format' };
  const domain = email.split('@')[1];
  if (FREE_EMAIL_PROVIDERS.has(domain)) return { ok: false, reason: 'free_provider' };
  return { ok: true };
}

function validatePhone(value) {
  const trimmed = (value || '').trim();
  if (trimmed.length < 6 || trimmed.length > 50) return false;
  return PHONE_REGEX.test(trimmed);
}

// UTM-værdier kommer fra URL'en og er derfor input enhver kan sætte. De ender inde i en tekst
// der sendes videre til CRM'et, så de saniteres før brug: alt uden for det tilladte tegnsæt
// fjernes, og længden begrænses. Uden det kunne et konstrueret link injicere klammer, pipes,
// linjeskift eller vilkårlig prosa ind i sagerne hos salg.
const UTM_MAX_LENGTH = 40;
const UTM_DISALLOWED = /[^A-Za-z0-9_.-]/g;

function sanitizeUtm(value) {
  if (typeof value !== 'string') return '';
  return value.replace(UTM_DISALLOWED, '').slice(0, UTM_MAX_LENGTH);
}

// ============================================
// WEB COMPONENT
// ============================================

class B2BLeadForm extends HTMLElement {
  constructor() {
    super();
    this.mountedAt = Date.now();
    this.hasInteracted = false;
    this.currentStep = 1;
    this.step1Value = null;
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.banner = this.querySelector('[data-banner]');
    this.formWrapper = this.querySelector('[data-form-wrapper]');
    this.successEl = this.querySelector('[data-success]');
    this.step1El = this.querySelector('[data-step="1"]');
    this.step2El = this.querySelector('[data-step="2"]');
    this.progressBars = this.querySelectorAll('[data-progress-bar]');
    this.progressLabel = this.querySelector('[data-progress-label]');
    this.nextButton = this.querySelector('[data-next]');
    this.backButton = this.querySelector('[data-back]');
    this.submitButton = this.querySelector('[data-submit]');
    this.resetButton = this.querySelector('[data-reset]');
    this.fields = {
      step1: this.querySelectorAll('input[name="step1_choice"]'),
      contact_name: this.querySelector('[data-field-contact-name]'),
      company_name: this.querySelector('[data-field-company-name]'),
      email: this.querySelector('[data-field-email]'),
      phone: this.querySelector('[data-field-phone]'),
      message: this.querySelector('[data-field-message]'),
      honeypot: this.querySelector('[data-field-honeypot]')
    };

    this.variant = this.dataset.variant || 'single';
    this.interest = this.dataset.interest || '';
    this.endpoint = this.dataset.endpoint || '';
    this.stubMode = this.dataset.stub === 'true';
    this.adAttribution = this.resolveAdAttribution();

    this.bindInteractionListeners();

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    this.fields.step1.forEach((radio) => {
      radio.addEventListener('change', this.onStep1Change.bind(this));
    });

    ['contact_name', 'company_name', 'email', 'phone', 'message'].forEach((key) => {
      const input = this.fields[key];
      if (input) {
        input.addEventListener('blur', () => this.validateField(key));
        input.addEventListener('input', () => this.clearFieldError(key));
      }
    });

    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.onNext.bind(this));
    }

    if (this.backButton) {
      this.backButton.addEventListener('click', this.onBack.bind(this));
    }

    if (this.resetButton) {
      this.resetButton.addEventListener('click', this.reset.bind(this));
    }

    this.updateStepUI();
  }

  bindInteractionListeners() {
    const handler = () => {
      this.hasInteracted = true;
      document.removeEventListener('mousemove', handler);
      document.removeEventListener('touchstart', handler);
      document.removeEventListener('keydown', handler);
    };
    document.addEventListener('mousemove', handler);
    document.addEventListener('touchstart', handler);
    document.addEventListener('keydown', handler);
  }

  /**
   * Finder annonce-attribution for dette besøg.
   *
   * Læser utm_source og utm_campaign fra URL'en og husker dem i sessionStorage, så attributionen
   * overlever at den besøgende klikker rundt på sitet inden formularen udfyldes. Findes de ikke i
   * URL'en, bruges det der allerede er husket. sessionStorage kan kaste i privat browsing — det
   * er ikke en fejl, attributionen gælder så bare den aktuelle sidevisning.
   */
  resolveAdAttribution() {
    const STORAGE_KEY = 'b2b-lead-form:ad-attribution';
    const params = new URLSearchParams(window.location.search);
    const fromUrl = {
      source: sanitizeUtm(params.get('utm_source')),
      campaign: sanitizeUtm(params.get('utm_campaign'))
    };

    if (fromUrl.source || fromUrl.campaign) {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl));
      } catch (error) {
        /* privat browsing — attributionen gælder kun denne sidevisning */
      }
      return fromUrl;
    }

    try {
      const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      if (stored && typeof stored === 'object') {
        return {
          source: sanitizeUtm(stored.source),
          campaign: sanitizeUtm(stored.campaign)
        };
      }
    } catch (error) {
      /* ugyldigt indhold i sessionStorage ignoreres */
    }

    return { source: '', campaign: '' };
  }

  // ============================================
  // STEP NAVIGATION (multi-step variant)
  // ============================================

  onStep1Change(event) {
    this.step1Value = event.target.value;
    if (this.nextButton) {
      this.nextButton.disabled = false;
    }

    // Auto-advance to step 2 after a brief delay so the user sees the
    // selection register visually. The race-condition guard inside the
    // timeout ensures rapid re-selections do not trigger multiple advances.
    if (this.variant === 'multi-step' && this.currentStep === 1) {
      const selectedAtClick = event.target.value;
      setTimeout(() => {
        if (
          this.variant === 'multi-step' &&
          this.currentStep === 1 &&
          this.step1Value === selectedAtClick
        ) {
          this.currentStep = 2;
          this.updateStepUI();
        }
      }, AUTO_ADVANCE_DELAY_MS);
    }
  }

  onNext(event) {
    event.preventDefault();
    if (this.variant !== 'multi-step') return;
    if (!this.step1Value) return;
    this.currentStep = 2;
    this.updateStepUI();
  }

  onBack(event) {
    event.preventDefault();
    if (this.variant !== 'multi-step') return;
    this.currentStep = 1;
    this.updateStepUI();
  }

  updateStepUI() {
    if (this.variant !== 'multi-step') return;

    if (this.step1El) this.step1El.hidden = this.currentStep !== 1;
    if (this.step2El) this.step2El.hidden = this.currentStep !== 2;

    this.progressBars.forEach((bar, index) => {
      const filled = index < this.currentStep;
      bar.classList.toggle('b2b-lead-form__progress-bar--empty', !filled);
    });

    if (this.progressLabel) {
      this.progressLabel.textContent = formatStepOf(this.currentStep, 2);
    }
  }

  // ============================================
  // VALIDATION
  // ============================================

  validateField(key) {
    const input = this.fields[key];
    if (!input) return true;
    const value = input.value;

    let valid = true;
    let messageKey = '';

    switch (key) {
      case 'contact_name':
      case 'company_name':
        if (!validateRequired(value)) { valid = false; messageKey = 'required'; }
        else if (!validateLength(value, 2, 255)) { valid = false; messageKey = 'too_short'; }
        break;
      case 'email': {
        if (!validateRequired(value)) { valid = false; messageKey = 'required'; break; }
        const result = validateEmail(value);
        if (!result.ok) {
          valid = false;
          messageKey = result.reason === 'free_provider' ? 'free_provider' : 'format';
        }
        break;
      }
      case 'phone':
        if (this.variant === 'notifikation') break;
        if (!validateRequired(value)) { valid = false; messageKey = 'required'; }
        else if (!validatePhone(value)) { valid = false; messageKey = 'format'; }
        break;
      case 'message':
        if (value && value.trim().length > 0 && !validateLength(value, 10, 2000)) {
          valid = false;
          messageKey = 'too_short';
        }
        break;
    }

    this.setFieldError(key, !valid, messageKey);
    return valid;
  }

  setFieldError(key, hasError, messageKey) {
    const input = this.fields[key];
    if (!input) return;
    const wrapper = input.closest('.b2b-lead-form__field');
    if (wrapper) {
      wrapper.dataset.invalid = hasError ? 'true' : 'false';
    }
    const errorEl = this.querySelector(`[data-error-for="${key}"]`);
    if (errorEl && hasError) {
      const camelKey = messageKey.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
      const datasetKey = `message${camelKey.charAt(0).toUpperCase()}${camelKey.slice(1)}`;
      const labels = getLabels();
      errorEl.textContent = errorEl.dataset[datasetKey] || errorEl.dataset.messageDefault || labels.invalid_value || 'Invalid value';
    }
  }

  clearFieldError(key) {
    const input = this.fields[key];
    if (!input) return;
    const wrapper = input.closest('.b2b-lead-form__field');
    if (wrapper) {
      wrapper.dataset.invalid = 'false';
    }
  }

  validateAll() {
    const required = this.variant === 'notifikation'
      ? ['email']
      : ['contact_name', 'company_name', 'email', 'phone'];

    let allValid = true;
    required.forEach((key) => {
      if (!this.validateField(key)) allValid = false;
    });

    if (this.fields.message && this.fields.message.value.trim().length > 0) {
      if (!this.validateField('message')) allValid = false;
    }

    return allValid;
  }

  // ============================================
  // SUBMIT
  // ============================================

  onSubmit(event) {
    event.preventDefault();
    this.hideBanner();

    // Honeypot check
    if (this.fields.honeypot && this.fields.honeypot.value !== '') {
      this.showSuccess();
      return;
    }

    // Time-gate
    if (Date.now() - this.mountedAt < TIME_GATE_MS) {
      this.showBanner(this.getErrorText('time_gate'));
      return;
    }

    // Interaction-gate
    if (!this.hasInteracted) {
      this.showSuccess();
      return;
    }

    // For multi-step, ensure we're on step 2
    if (this.variant === 'multi-step' && this.currentStep !== 2) {
      return;
    }

    if (!this.validateAll()) {
      return;
    }

    this.submit();
  }

  async submit() {
    this.setBusy(true);

    const payload = this.buildPayload();

    // TODO(CRM): When CRM endpoint is confirmed live and CORS is configured, set
    // data-stub="false" on the <b2b-lead-form> element in the section template.
    if (this.stubMode || !this.endpoint) {
      console.log('[b2b-lead-form] Stub submission — NOT sent to CRM:', payload);
      await new Promise((resolve) => setTimeout(resolve, 400));
      this.setBusy(false);
      this.showSuccess();
      return;
    }

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const body = await response.json().catch(() => ({
        ok: false,
        error: 'server',
        message: this.getErrorText('server')
      }));

      if (response.ok && body.ok) {
        this.setBusy(false);
        // Ignore CRM's server message — it is always Danish.
        // Use the localized markup in the success div instead.
        this.showSuccess();
        this.trackLead();
        return;
      }

      this.handleErrorResponse(response.status, body);
    } catch (error) {
      console.error('[b2b-lead-form] Network error:', error);
      this.showBanner(this.getErrorText('network'));
    } finally {
      this.setBusy(false);
    }
  }

  buildPayload() {
    const isNotification = this.variant === 'notifikation';

    return {
      company_name: isNotification ? 'Notifikation' : (this.fields.company_name?.value?.trim() || ''),
      contact_name: isNotification ? (this.fields.contact_name?.value?.trim() || 'Notifikation') : (this.fields.contact_name?.value?.trim() || ''),
      email: (this.fields.email?.value || '').trim().toLowerCase(),
      phone: isNotification ? NOTIFICATION_PLACEHOLDER_PHONE : (this.fields.phone?.value?.trim() || NOTIFICATION_PLACEHOLDER_PHONE),
      message: this.buildMessage(),
      company_size: this.step1Value || undefined
    };
  }

  /**
   * Interesse-etiketten, udvidet med annonce-attribution når den findes.
   * "Ads" bliver til "Ads: meta/sommer26". Uden UTM'er returneres etiketten uændret,
   * så alle eksisterende sider beholder præcis den præfiks de har i dag.
   */
  interestWithAttribution() {
    const source = this.adAttribution?.source || '';
    const campaign = this.adAttribution?.campaign || '';
    if (!source && !campaign) return this.interest;

    const detail = source && campaign ? `${source}/${campaign}` : (source || campaign);
    return `${this.interest}: ${detail}`;
  }

  buildMessage() {
    const label = this.interestWithAttribution();
    const interestPrefix = this.step1Value
      ? `[${label} | ${this.step1Value}]`
      : `[${label}]`;

    if (this.variant === 'notifikation') {
      return `${interestPrefix} Tilmeldt waitlist`;
    }

    const userMessage = this.fields.message?.value?.trim() || 'Ingen besked';
    return `${interestPrefix} ${userMessage}`;
  }

  handleErrorResponse(status, body) {
    if (status === 400 && body.error) {
      this.setFieldError(body.error, true, 'server');
      const errorEl = this.querySelector(`[data-error-for="${body.error}"]`);
      if (errorEl && body.message) {
        errorEl.textContent = body.message;
      }
      this.showBanner(body.message || this.getErrorText('server'));
      return;
    }

    if (status === 429) {
      this.showBanner(body.message || this.getErrorText('rate_limit'));
      return;
    }

    this.showBanner(body.message || this.getErrorText('server'));
  }

  setBusy(busy) {
    const btn = this.submitButton || this.nextButton;
    if (!btn) return;
    btn.disabled = busy;
    if (busy) {
      btn.setAttribute('aria-busy', 'true');
      btn.dataset.originalText = btn.textContent;
      const labels = getLabels();
      btn.textContent = this.dataset.submitBusyText || labels.submit_busy || 'Sending...';
    } else {
      btn.removeAttribute('aria-busy');
      if (btn.dataset.originalText) {
        btn.textContent = btn.dataset.originalText;
        delete btn.dataset.originalText;
      }
    }
  }

  // ============================================
  // UI HELPERS
  // ============================================

  /**
   * Melder en bekræftet henvendelse til Google Tag Manager, som sender den videre som
   * LinkedIn-konvertering. Kaldes kun når CRM har svaret ok, så spam og fejl ikke tælles.
   * Ingen persondata i hændelsen.
   */
  trackLead() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'b2b_lead_submit',
      b2b_variant: this.variant,
      b2b_interest: this.interest,
      b2b_company_size: this.step1Value || ''
    });
  }

  showSuccess(/* serverMessage — intentionally ignored. CRM returns Danish text;
     we keep the localized markup in the success div instead. */) {
    if (this.formWrapper) this.formWrapper.hidden = true;
    if (this.successEl) {
      this.successEl.hidden = false;
    }
  }

  reset() {
    if (this.form) this.form.reset();
    if (this.formWrapper) this.formWrapper.hidden = false;
    if (this.successEl) this.successEl.hidden = true;
    this.hideBanner();
    this.currentStep = 1;
    this.step1Value = null;
    this.updateStepUI();
    this.mountedAt = Date.now();
    this.hasInteracted = false;
    this.bindInteractionListeners();
    if (this.nextButton) this.nextButton.disabled = true;
  }

  showBanner(message) {
    if (!this.banner) return;
    this.banner.textContent = message;
    this.banner.dataset.variant = 'error';
  }

  hideBanner() {
    if (!this.banner) return;
    this.banner.textContent = '';
    delete this.banner.dataset.variant;
  }

  getErrorText(key) {
    const camelKey = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    const datasetKey = `error${camelKey.charAt(0).toUpperCase()}${camelKey.slice(1)}`;
    const labels = getLabels();
    const prefix = labels.error_prefix || 'Error';
    return this.dataset[datasetKey] || `${prefix}: ${key}`;
  }
}

if (!customElements.get('b2b-lead-form')) {
  customElements.define('b2b-lead-form', B2BLeadForm);
}
